import { StyleSheet, Text, TextStyle, View, ViewProps } from 'react-native'

import { tokens } from '../../theme/tokens'

export type BadgeVariant = 'default' | 'compact'

export interface BadgeProps extends ViewProps {
  /**
   * Badge text. Rendered uppercase. A nullish label renders an empty pill,
   * preserving CWA `InstructionsBadge` behavior for optional labels.
   * Falls back to `children` — required by CMA call sites where i18n `Trans`
   * injects the text as children (e.g. `components={{ badge: <Badge /> }}`).
   */
  label?: string | null | undefined
  /**
   * `default` — the CMA pill (13px, radius 6).
   * `compact` — the CWA instructions pill (10px, radius 4, tight tracking).
   */
  variant?: BadgeVariant
  backgroundColor?: string
  textStyle?: TextStyle
}

/**
 * Shared Badge component.
 *
 * Reconciles CMA's `Badge` and CWA's `InstructionsBadge` (the `compact`
 * variant). Renders a small pill with uppercase text. Defaults to the brand
 * info-blue background.
 */
export const Badge = ({
  label,
  variant = 'default',
  backgroundColor,
  textStyle,
  style,
  children,
  ...passthroughProps
}: BadgeProps) => {
  const compact = variant === 'compact'
  return (
    <View
      {...passthroughProps}
      style={[
        styles.container,
        compact && styles.compactContainer,
        backgroundColor ? { backgroundColor } : undefined,
        style,
      ]}
    >
      <Text style={[styles.text, compact && styles.compactText, textStyle]}>{label ?? children}</Text>
    </View>
  )
}

Badge.displayName = 'Badge'

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: tokens.colors.brand.infoBlue,
    alignSelf: 'flex-start',
  },
  compactContainer: {
    borderRadius: 4,
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 1,
  },
  text: {
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    color: tokens.colors.brand.midnight,
  },
  compactText: {
    fontSize: 10,
    fontWeight: '700',
    // 0.02em at the 10px font size, restated in px — RN has no em unit.
    letterSpacing: 0.2,
    lineHeight: 13,
    textAlign: 'left',
    color: tokens.colors.brand.textSoftBlack,
  },
})
